import { useMemo, useEffect, useState } from 'react';
import { useUpdateDocumentTitle } from '@anytag/shared-hooks';
import { Button } from '@anytag/shared-atoms';
import { useStore } from '@tanstack/react-store';
import { store, actions, DEFAULT_CAMPAIGN } from './store';
import { useCampaigns } from './hooks/use-campaigns';
import { useVideoUpload } from './hooks/use-video-upload';
import { useAnalysis } from './hooks/use-analysis';
import { RuleBuilder } from './components/rules/rule-builder';
import { AnalysisReport } from './components/analysis/analysis-report';
import { VideoList } from './components/video/video-list';

export const VideoCompAI = () => {
  useUpdateDocumentTitle({ title: 'appHeader.VideoComp AI' });

  const state = useStore(store, (state) => state);
  const isDirty = useMemo(() => {
    if (state.campaigns.length !== 1) return true;
    const c = state.campaigns[0];
    if (c.id !== DEFAULT_CAMPAIGN.id) return true;
    if (c.videos.length > 0) return true;
    if (c.context !== DEFAULT_CAMPAIGN.context) return true;

    return false;
  }, [state]);

  const onReset = () => {
    if (window.confirm('Reset all state to default? This cannot be undone.')) {
      actions.resetStore();
    }
  };

  const {
    campaigns,
    selectedCampaignId,
    setSelectedCampaignId,
    activeCampaign,
    videoItems,
    createCampaign,
    deleteCampaign,
    updateCampaign,
    updateActiveCampaignVideos,
    updateVideoStatus
  } = useCampaigns();

  const { processFiles } = useVideoUpload({
    currentCount: videoItems.length,
    onUpload: (newItems) => updateActiveCampaignVideos([...videoItems, ...newItems]),
    updateVideoStatus,
    onError: (err) => {
      setUploadError(err);
    }
  });

  const [uploadError, setUploadError] = useState<string | null>(null);

  const onRemoveVideo = (id: string) => {
    const newVideos = videoItems.filter((i) => i.id !== id);
    updateActiveCampaignVideos(newVideos);
  };

  const onClearHistory = () => {
    if (window.confirm('Are you sure you want to clear history for this Task?')) {
      const newVideos = videoItems.filter((i) => i.file !== undefined);
      updateActiveCampaignVideos(newVideos);
    }
  };

  const { isAnalyzing, processingIndex, analysisError, activeRules, runAnalysis } = useAnalysis({
    activeCampaign,
    videoItems,
    updateActiveCampaignVideos,
    updateVideoStatus
  });

  const globalError = uploadError || analysisError;

  useEffect(() => {
    if (uploadError) {
      const timer = setTimeout(() => setUploadError(null), 5000);
      return () => clearTimeout(timer);
    }
  }, [uploadError]);

  return (
    <div className="flex min-h-screen flex-col p-4 text-primary">
      <header className="mb-6 flex items-center justify-between px-2">
        <div>
          <h1 className="bg-linear-to-r from-red-60 via-red-30 to-red-80 bg-clip-text text-2xl font-extrabold text-transparent">
            VideoComp AI
          </h1>
          <p className="text-sm text-primary">Automated visual compliance & content auditor</p>
        </div>
        <div className="flex items-center gap-4">
          {isDirty && (
            <Button variant="outline" onClick={onReset} size="sm" className="px-2">
              Clear All
            </Button>
          )}
          <div className="hidden text-right md:block">
            <div className="text-xs text-primary">Powered by</div>
            <div className="font-semibold text-primary">Google Gemini 2.5</div>
          </div>
        </div>
      </header>

      <main className="mx-auto flex w-full flex-1 flex-col gap-4 lg:h-[calc(100vh-120px)]">
        <div className="grid grid-cols-1 gap-4 lg:h-[50vh] lg:min-h-[450px] lg:grid-cols-12">
          <div className="overflow-y-auto lg:col-span-4">
            <VideoList
              videoItems={videoItems}
              isAnalyzing={isAnalyzing}
              onRemove={onRemoveVideo}
              onClearHistory={onClearHistory}
              globalError={globalError}
              processingIndex={processingIndex}
              onAnalyze={runAnalysis}
              onFilesAdded={processFiles}
              onFileError={setUploadError}
            />
          </div>

          <div className="h-full lg:col-span-8">
            <RuleBuilder
              campaigns={campaigns}
              selectedId={selectedCampaignId}
              activeCampaign={activeCampaign}
              onSelect={setSelectedCampaignId}
              onCreate={createCampaign}
              onDelete={deleteCampaign}
              onUpdate={updateCampaign}
            />
          </div>
        </div>

        <div className="min-h-0 flex-1">
          <AnalysisReport items={videoItems} rules={activeRules} isLoading={isAnalyzing} />
        </div>
      </main>
    </div>
  );
};
